import { useCallback } from 'react'
import { useSnackbar } from 'notistack'
import { t } from '@lingui/macro'

import { parseEmailAttachments } from '../lib/xmlParser'
import { useSavePatientFile } from './useSavePatientFile'

export function useEmailAttachmentNotification(patientId: string) {
  const { saveFile } = useSavePatientFile(patientId)
  const { enqueueSnackbar } = useSnackbar()

  const onEmailAttachmentNotification = useCallback(
    async (xmlData: string) => {
      try {
        const attachments = parseEmailAttachments(xmlData)
        console.log('[EmailAttachment] Received:', attachments.length, 'attachment(s)')

        if (attachments.length === 0) {
          console.log('[EmailAttachment] No attachments in email notification')
          return
        }

        let savedCount = 0
        const errors: string[] = []

        for (const attachment of attachments) {
          try {
            console.log('[EmailAttachment] Processing attachment:', {
              fileName: attachment.fileName,
              contentType: attachment.contentType,
              size: attachment.size,
            })

            if (!attachment.contentBase64) {
              throw new Error('Empty attachment content')
            }

            // Decode base64
            const byteString = atob(attachment.contentBase64)
            const uint8Array = new Uint8Array(byteString.length)
            for (let i = 0; i < byteString.length; i++) {
              uint8Array[i] = byteString.charCodeAt(i)
            }

            const file = new File([uint8Array], attachment.fileName, { type: attachment.contentType })
            await saveFile(file)
            savedCount++
            console.log('[EmailAttachment] Attachment saved successfully:', attachment.fileName)
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error'
            console.error('[EmailAttachment] Failed to save attachment:', attachment.fileName, message)
            errors.push(`${attachment.fileName}: ${message}`)
          }
        }

        if (savedCount > 0) {
          enqueueSnackbar(t`${savedCount} attachment(s) added successfully`, { variant: 'success' })
        }
        if (errors.length > 0) {
          enqueueSnackbar(t`Failed to save ${errors.length} attachment(s)`, { variant: 'error' })
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error'
        console.error('[EmailAttachment] Failed to process email:', message)
        enqueueSnackbar(t`Failed to save attachments: ${message}`, { variant: 'error' })
      }
    },
    [saveFile, enqueueSnackbar]
  )

  return { onEmailAttachmentNotification }
}
